// src/pages/ManageTopicPage.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import Papa from 'papaparse';

const slugify = (s) =>
  (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

export default function ManageTopicPage() {
  const [courses, setCourses] = useState([]); // [{name, alias}]
  const [selectedCourse, setSelectedCourse] = useState(''); // course name (stored in topics.course)
  const [selectedCourseAlias, setSelectedCourseAlias] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [newCourseAlias, setNewCourseAlias] = useState('');
  const [topics, setTopics] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [dropdownOptions, setDropdownOptions] = useState({ level0: [], level1: [], level2: [] });
  const [filters, setFilters] = useState({ level0: '', level1: '', level2: '' });
  const [csvFile, setCsvFile] = useState(null);

  // Manual hierarchy form
  const [newHierarchy, setNewHierarchy] = useState({
    level0: '',
    level1: '',
    level2: '',
    level3: '',
    module_code: '',
  });

  // ---------- Courses ----------
  const loadCourses = async () => {
    // Prefer courses table (has alias)
    const { data, error } = await supabase
      .from('courses')
      .select('name, alias')
      .order('name', { ascending: true });

    if (!error && data) {
      setCourses(data);
      return;
    }

    // Fallback from view (no alias there)
    const { data: vdata, error: verror } = await supabase
      .from('subtopic_summary')
      .select('course');

    if (!verror && vdata) {
      const unique = [...new Set(vdata.map((r) => r.course))]
        .filter(Boolean)
        .sort()
        .map((name) => ({ name, alias: slugify(name) }));
      setCourses(unique);
    }
  };

  // Insert→update fallback (no need for onConflict to be configured)
 // Select-first strategy to avoid 409 conflicts
const ensureCourse = async (name, alias) => {
  if (!name) return;
  const normalized = {
    name: name.trim(),
    alias: (alias || slugify(name)).trim(),
  };

  // 1) Does it exist?
  const { data: existing, error: selErr } = await supabase
    .from('courses')
    .select('name, alias')
    .eq('name', normalized.name)
    .maybeSingle();

  if (selErr) {
    console.warn('ensureCourse select error:', selErr?.message);
    return;
  }

  // 2) Update alias if course exists but alias changed
  if (existing) {
    if (existing.alias !== normalized.alias) {
      const { error: updErr } = await supabase
        .from('courses')
        .update({ alias: normalized.alias })
        .eq('name', normalized.name);
      if (updErr) console.warn('ensureCourse update error:', updErr?.message);
    }
    return;
  }

  // 3) Insert new
  const { error: insErr } = await supabase
    .from('courses')
    .insert([normalized]);

  if (insErr) {
    console.warn('ensureCourse insert error:', insErr?.message);
  }
};


  // ---------- View / Topics ----------
  const loadTopics = async (courseName) => {
    setLoading(true);
    const { data, error } = await supabase
      .from('subtopic_summary')
      .select('*')
      .eq('course', courseName);

    setLoading(false);

    if (error) {
      setMessage('❌ Failed to load topics.');
      setTopics([]);
      setFilteredTopics([]);
      setDropdownOptions({ level0: [], level1: [], level2: [] });
      return;
    }

    const rows = data || [];
    setTopics(rows);
    setFilteredTopics(rows);

    if (rows.length === 0) {
      setMessage('⚠️ No hierarchy found. Please create it first.');
      setDropdownOptions({ level0: [], level1: [], level2: [] });
    } else {
      setMessage('');
      const levels = { level0: [], level1: [], level2: [] };
      rows.forEach((r) => {
        if (!levels.level0.includes(r.level0_name)) levels.level0.push(r.level0_name);
        if (!levels.level1.includes(r.topic_name)) levels.level1.push(r.topic_name);
        if (!levels.level2.includes(r.module_name)) levels.level2.push(r.module_name);
      });
      setDropdownOptions(levels);
    }

    setFilters({ level0: '', level1: '', level2: '' });
  };

  // ---------- Filter table ----------
  const filterTable = (level0, level1, level2) => {
    let f = topics;
    if (level0) f = f.filter((t) => t.level0_name === level0);
    if (level1) f = f.filter((t) => t.topic_name === level1);
    if (level2) f = f.filter((t) => t.module_name === level2);
    setFilteredTopics(f);
  };

  useEffect(() => {
    filterTable(filters.level0, filters.level1, filters.level2);
  }, [filters, topics]);

  // ---------- Create course ----------
  const handleCreateCourse = async () => {
    const name = newCourseName.trim();
    let alias = newCourseAlias.trim();
    if (!name) return;
    if (!alias) alias = slugify(name);

    await ensureCourse(name, alias);
    setNewCourseName('');
    setNewCourseAlias('');
    setMessage(`✅ Course "${name}" created. Route: /${alias}`);

    await loadCourses();
    setSelectedCourse(name);
    setSelectedCourseAlias(alias);

    // Clear current view (brand-new course likely has no hierarchy yet)
    setTopics([]);
    setFilteredTopics([]);
    setDropdownOptions({ level0: [], level1: [], level2: [] });
    setFilters({ level0: '', level1: '', level2: '' });
  };

  // ---------- Insert hierarchy nodes into topics ----------
  const insertTopic = async (name, level, parent_id = null, courseOverride = null, moduleCode = null) => {
    const payload = {
      name,
      level,
      parent_id,
      course: courseOverride || selectedCourse, // store course text in topics
      module_code: level === 2 ? (moduleCode ?? newHierarchy.module_code) || null : null,
    };
    const { data, error } = await supabase.from('topics').insert([payload]).select().single();
    if (error) throw error;
    return data.id;
  };

const handleCreateHierarchy = async () => {
  try {
    if (!selectedCourse) {
      setMessage('❌ Please select or create a course first.');
      return;
    }

    // basic validation
    const nh = {
      level0: (newHierarchy.level0 || '').trim(),
      level1: (newHierarchy.level1 || '').trim(),
      level2: (newHierarchy.level2 || '').trim(),
      level3: (newHierarchy.level3 || '').trim(),
      module_code: (newHierarchy.module_code || '').trim(),
    };
    if (!nh.level0 || !nh.level1 || !nh.level2 || !nh.level3) {
      setMessage('❌ Please fill Level 0, Level 1, Level 2, and Level 3.');
      return;
    }

    // make sure the course row exists/updated
    await ensureCourse(selectedCourse, selectedCourseAlias || slugify(selectedCourse));

    // IMPORTANT: reuse if exists, otherwise create
    const id0 = await getOrCreateTopic({
      name: nh.level0,
      level: 0,
      parent_id: null,
      course: selectedCourse,
    });

    const id1 = await getOrCreateTopic({
      name: nh.level1,
      level: 1,
      parent_id: id0,
      course: selectedCourse,
    });

    const id2 = await getOrCreateTopic({
      name: nh.level2,
      level: 2,
      parent_id: id1,
      course: selectedCourse,
      module_code: nh.module_code || null,
    });

    await getOrCreateTopic({
      name: nh.level3,
      level: 3,
      parent_id: id2,
      course: selectedCourse,
    });

    setMessage('✅ Hierarchy created (reused existing levels when found).');
    setNewHierarchy({ level0: '', level1: '', level2: '', level3: '', module_code: '' });

    // refresh UI
    await loadCourses();
    await loadTopics(selectedCourse);
  } catch (err) {
    console.error(err);
    setMessage('❌ Failed to create hierarchy.');
  }
};


  const handleResetForm = () => {
    setNewHierarchy({ level0: '', level1: '', level2: '', level3: '', module_code: '' });
    setDropdownOptions((prev) => ({ level0: prev.level0, level1: [], level2: [] }));
  };

  // ---------- Dependent inputs (manual entry) ----------
  const handleLevel0Change = (val) => {
    const level1 = topics.filter((t) => t.level0_name === val).map((t) => t.topic_name);
    setNewHierarchy({ level0: val, level1: '', level2: '', level3: '', module_code: '' });
    setDropdownOptions((prev) => ({ ...prev, level1: [...new Set(level1)], level2: [] }));
  };

  const handleLevel1Change = (val) => {
    const level2 = topics
      .filter((t) => t.level0_name === newHierarchy.level0 && t.topic_name === val)
      .map((t) => t.module_name);
    setNewHierarchy((prev) => ({ ...prev, level1: val, level2: '', level3: '' }));
    setDropdownOptions((prev) => ({ ...prev, level2: [...new Set(level2)] }));
  };
  // Find an existing topic by (name, level, parent_id, course). If not found, create it.
// Also updates module_code when level=2 and a code is provided.
const getOrCreateTopic = async ({ name, level, parent_id = null, course, module_code = null }) => {
  let query = supabase
    .from('topics')
    .select('id, module_code')
    .eq('name', name)
    .eq('level', level)
    .eq('course', course)
    .limit(1);

  if (parent_id === null) {
    query = query.is('parent_id', null);
  } else {
    query = query.eq('parent_id', parent_id);
  }

  const { data: found, error: findErr } = await query;
  if (findErr) {
    console.warn('getOrCreateTopic find error:', findErr?.message);
  }

  if (found && found.length) {
    const id = found[0].id;
    if (level === 2 && module_code && found[0].module_code !== module_code) {
      await supabase.from('topics').update({ module_code }).eq('id', id);
    }
    return id;
  }

  const payload = {
    name,
    level,
    parent_id,
    course,
    module_code: level === 2 ? module_code || null : null,
  };

  const { data, error } = await supabase
    .from('topics')
    .insert([payload])
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
};


  // ---------- CSV Upload (selected course only) ----------
  // Expected CSV headers: level0,level1,level2,level3,module_code
// Expected CSV headers (any spacing/case is ok): level0, level1, level2, level3, module_code
const handleCsvUpload = async () => {
  if (!csvFile) {
    setMessage('❌ Please choose a CSV file first.');
    return;
  }
  if (!selectedCourse) {
    setMessage('❌ Please select (or create) a course before uploading.');
    return;
  }

  await ensureCourse(selectedCourse, selectedCourseAlias || slugify(selectedCourse));

  Papa.parse(csvFile, {
    header: true,
    skipEmptyLines: true,
    // Normalize headers: trim, drop BOM, lowercase, remove internal spaces/quotes
    transformHeader: (h) =>
      h
        .replace(/^\uFEFF/, '')     // strip BOM
        .replace(/"/g, '')          // remove stray quotes
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ''),       // e.g. " level1" -> "level1"
    complete: async ({ data }) => {
      try {
        let imported = 0;
        let skipped = 0;

        for (const raw of data) {
          // Normalize cell values
          const row = {
            level0: (raw.level0 ?? '').toString().trim(),
            level1: (raw.level1 ?? '').toString().trim(),
            level2: (raw.level2 ?? '').toString().trim(),
            level3: (raw.level3 ?? '').toString().trim(),
            module_code: (raw.module_code ?? '').toString().trim() || null,
          };

          if (!(row.level0 && row.level1 && row.level2 && row.level3)) {
            skipped++;
            console.warn('Skipping incomplete CSV row:', raw);
            continue;
          }

          // Build chain (re-use existing nodes if they already exist)
          const id0 = await getOrCreateTopic({
            name: row.level0, level: 0, parent_id: null, course: selectedCourse,
          });

          const id1 = await getOrCreateTopic({
            name: row.level1, level: 1, parent_id: id0, course: selectedCourse,
          });

          const id2 = await getOrCreateTopic({
            name: row.level2, level: 2, parent_id: id1, course: selectedCourse, module_code: row.module_code,
          });

          await getOrCreateTopic({
            name: row.level3, level: 3, parent_id: id2, course: selectedCourse,
          });

          imported++;
        }

        setMessage(`✅ CSV Upload: ${imported} rows imported${skipped ? `, ${skipped} skipped` : ''}.`);
        await loadCourses();
        await loadTopics(selectedCourse);
      } catch (err) {
        console.error(err);
        setMessage('❌ Error uploading CSV');
      }
    },
  });
};

  // ---------- Effects ----------
  useEffect(() => {
    loadCourses();
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Manage Topics & Courses</h1>

      {/* Course Selector & Creator */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="block text-sm font-medium mb-1">Select Course</label>
          <select
            className="border p-2 rounded w-full"
            value={selectedCourse}
            onChange={(e) => {
              const name = e.target.value;
              setSelectedCourse(name);
              const found = courses.find((c) => c.name === name);
              setSelectedCourseAlias(found?.alias || slugify(name));
              if (name) {
                loadTopics(name);
              } else {
                setTopics([]);
                setFilteredTopics([]);
                setDropdownOptions({ level0: [], level1: [], level2: [] });
                setFilters({ level0: '', level1: '', level2: '' });
              }
            }}
          >
            <option value="">-- Select Course --</option>
            {courses.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} {c.alias ? `(${c.alias})` : ''}
              </option>
            ))}
          </select>
          {selectedCourse && (
            <div className="text-xs text-gray-600 mt-1">Route: /{selectedCourseAlias}</div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">New Course Name</label>
          <input
            type="text"
            className="border p-2 rounded w-full"
            placeholder="e.g., FRM-2"
            value={newCourseName}
            onChange={(e) => {
              const v = e.target.value;
              setNewCourseName(v);
              if (!newCourseAlias || newCourseAlias === slugify(newCourseName)) {
                setNewCourseAlias(slugify(v));
              }
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Course Alias (URL)</label>
          <input
            type="text"
            className="border p-2 rounded w-full"
            placeholder="e.g., frm2"
            value={newCourseAlias}
            onChange={(e) => setNewCourseAlias(slugify(e.target.value))}
          />
          {newCourseAlias && (
            <div className="text-xs text-gray-600 mt-1">Preview: /{newCourseAlias}</div>
          )}
        </div>

        <div className="flex md:block">
          <button className="bg-green-600 text-white px-4 py-2 rounded w-full" onClick={handleCreateCourse}>
            Add Course
          </button>
        </div>
      </div>

      {/* Manual Hierarchy Entry */}
      {selectedCourse && (
        <div className="mb-8 border p-4 rounded bg-gray-50">
          <h3 className="text-lg font-semibold mb-3">
            ➕ Create Hierarchy under {selectedCourse}{' '}
            <span className="text-sm text-gray-500">(/ {selectedCourseAlias})</span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              className="border p-2 rounded"
              placeholder="Level 0 (Subject)"
              list="level0-options"
              value={newHierarchy.level0}
              onChange={(e) => handleLevel0Change(e.target.value)}
            />
            <datalist id="level0-options">
              {dropdownOptions.level0.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>

            <input
              className="border p-2 rounded"
              placeholder="Level 1 (Chapter)"
              list="level1-options"
              value={newHierarchy.level1}
              onChange={(e) => handleLevel1Change(e.target.value)}
            />
            <datalist id="level1-options">
              {dropdownOptions.level1.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>

            <input
              className="border p-2 rounded"
              placeholder="Level 2 (Topic)"
              list="level2-options"
              value={newHierarchy.level2}
              onChange={(e) => setNewHierarchy({ ...newHierarchy, level2: e.target.value })}
            />
            <datalist id="level2-options">
              {dropdownOptions.level2.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>

            <input
              className="border p-2 rounded"
              placeholder="Level 3 (Subtopic)"
              value={newHierarchy.level3}
              onChange={(e) => setNewHierarchy({ ...newHierarchy, level3: e.target.value })}
            />

            <input
              className="border p-2 rounded"
              placeholder="Module Code (optional)"
              value={newHierarchy.module_code}
              onChange={(e) => setNewHierarchy({ ...newHierarchy, module_code: e.target.value })}
            />
          </div>

          <div className="mt-4 flex gap-4">
            <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={handleCreateHierarchy}>
              ➕ Create Hierarchy
            </button>
            <button className="bg-gray-500 text-white px-4 py-2 rounded" onClick={handleResetForm}>
              🔄 Reset
            </button>
          </div>

          <div className="mt-6">
            <h4 className="font-semibold mb-2">📤 Bulk Upload via CSV</h4>
            <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} />
            <button className="ml-4 bg-purple-600 text-white px-4 py-2 rounded" onClick={handleCsvUpload}>
              ⬆️ Upload CSV
            </button>
            <div className="text-xs text-gray-600 mt-2">
              CSV columns: <code>level0</code>, <code>level1</code>, <code>level2</code>, <code>level3</code>, <code>module_code</code><br />
              Course will be the currently selected course: <b>{selectedCourse || '(not selected yet)'}</b>
            </div>
          </div>
        </div>
      )}

      {message && <div className="text-blue-800 mb-4">{message}</div>}
      {loading && <p>Loading...</p>}

      {/* Table + Filters */}
      {!loading && selectedCourse && (
        <>
          <div className="mb-4 flex flex-wrap gap-4">
            <select
              className="border p-2 rounded"
              value={filters.level0}
              onChange={(e) => setFilters((prev) => ({ ...prev, level0: e.target.value }))}
            >
              <option value="">All Level 0</option>
              {dropdownOptions.level0.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>

            <select
              className="border p-2 rounded"
              value={filters.level1}
              onChange={(e) => setFilters((prev) => ({ ...prev, level1: e.target.value }))}
            >
              <option value="">All Level 1</option>
              {dropdownOptions.level1.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>

            <select
              className="border p-2 rounded"
              value={filters.level2}
              onChange={(e) => setFilters((prev) => ({ ...prev, level2: e.target.value }))}
            >
              <option value="">All Level 2</option>
              {dropdownOptions.level2.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {filteredTopics.length > 0 ? (
            <table className="table-auto w-full border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 border">Level 0</th>
                  <th className="px-3 py-2 border">Topic</th>
                  <th className="px-3 py-2 border">Module</th>
                  <th className="px-3 py-2 border">Outline</th>
                  <th className="px-3 py-2 border">Module Code</th>
                </tr>
              </thead>
              <tbody>
                {filteredTopics.map((t) => (
                  <tr key={t.outline_id}>
                    <td className="px-3 py-2 border">{t.level0_name}</td>
                    <td className="px-3 py-2 border">{t.topic_name}</td>
                    <td className="px-3 py-2 border">{t.module_name}</td>
                    <td className="px-3 py-2 border">{t.outline_name}</td>
                    <td className="px-3 py-2 border">{t.module_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-gray-600">No rows yet for this course.</div>
          )}
        </>
      )}
    </div>
  );
}
